import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {useTable, useGroupBy, useTableState, useGlobalFilter, useAsyncDebounce, useSortBy, useFilters, useExpanded, usePagination,} from "react-table";
import {Table, Row, Col, Button, Input, CardBody, PaginationItem, PaginationLink,} from "reactstrap";
import { Filter, DefaultColumnFilter } from "../Common/Filter";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";
import { useNavigate } from "react-router";


interface GlobalFilterProps {
  preGlobalFilteredRows?: any;
  globalFilter?: any;
  setGlobalFilter?: any;
  isJobListGlobalFilter?: any;
}

// Define a default UI for filtering
function GlobalFilter({preGlobalFilteredRows, globalFilter, setGlobalFilter,
  isJobListGlobalFilter,
}: GlobalFilterProps) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce((value: any) => {
    setGlobalFilter(value || undefined);
  }, 200);
  return (
    <React.Fragment>
      <Col md={5}>
        <div className="search-box me-xxl-2 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Pesquisar
              </span>
              <input
                onChange={e => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} registos...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && <JobListGlobalFilter />}
    </React.Fragment>
  );
}

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  isJobListGlobalFilter?: any;
  isAddOptions?: any;
  isAddUserList?: any;
  handleUserClick?: any;
  handleOrderClicks?: any;
  handleCustomerClick?: any;
  isAddCustList?: any;
  customPageSize?: any;
  className?: any;
  customPageSizeOptions?: any;
  isAddInvoiceList?: any;
  handleInvoiceClick?: any;
  handleRowClick?: any;
  isOptionalAdd?: any;
  addButtonLabel?: string;
}

//const tableState = useTableState({ groupBy: ['departamento'] })

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleUserClick,
  handleOrderClicks,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  handleRowClick,
  isAddInvoiceList,
  handleInvoiceClick,
  addButtonLabel,
  isOptionalAdd,
}: TableContainerProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        groupBy: ['departamento'],
        expanded: {},
        pageIndex: 0,        
        pageSize: customPageSize,
        //sortBy: [{desc: true,},],        
      },
    },    
    useGlobalFilter,
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = (column: any) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };
  const navigate = useNavigate();

  const onChangeInSelect = (event: any) => {
    setPageSize(Number(event.target.value));
  };

  const onChangeInInput = (event: any) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };
  return (
    <Fragment>
      <Row className="mb-2">        
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
          />
        )}        
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()} className={className}>
          <thead className="table-dark table-nowrap">
            {headerGroups.map((headerGroup: any, index: number) => (
              <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((columns: any) => (
                  <th key={columns.id}>
                    <div className="mb-2 text-white" {...columns.getSortByToggleProps()}>                      
                      {columns.render('Header')}
                      {generateSortingIndicator(columns)}
                    </div>
                    <Filter column={columns} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr>
                    {row.cells.map((cell: any, index: number) => {
                      const cellStyle = {
                        width: "auto",
                        background: cell.isGrouped
                          ? '#ffa50078'
                          : cell.isAggregated
                          ? '#ffa50078'
                          : 'white',
                        cursor: !cell.isGrouped ? 'pointer' : 'default',
                        fontWeight: cell.isGrouped ? 'bold' : 'normal',
                      };
                      return (
                        <td key={cell.id} {...cell.getCellProps()} style={cellStyle}
                          onClick={() => {
                            if (!cell.isGrouped) {
                              handleRowClick(row.original);
                            }
                          }}
                        >
                          {cell.isGrouped ? (
                              // If it's a grouped cell, add an expander and row count
                            <>
                              <span {...row.getToggleRowExpandedProps()}>
                                {row.isExpanded ? '👇' : '👉'}
                              </span>{' '}
                              {cell.render('Cell')} ({row.subRows.length})
                            </>
                          ) : cell.isAggregated ? (
                            // If the cell is aggregated, use the Aggregated
                            // renderer for cell
                            cell.render('Aggregated')
                          ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                            // Otherwise, just render the regular cell
                            cell.render('Cell')
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Row className="g-0">
        <Col sm={6}>
          <div>
            <p className="mb-sm-0">
              Mostrando {pageIndex * pageSize + 1} a{" "}
              {Math.min((pageIndex + 1) * pageSize, data.length)} de{" "}
              {data.length} entradas
            </p>
          </div>
        </Col>
        <Col sm={6}>
          <div className="float-sm-end">
            <ul className="pagination pagination-rounded mb-sm-0">
              <PaginationItem
                className={canPreviousPage ? "page-item" : "page-item disabled"}
              >
                <PaginationLink
                  onClick={() => gotoPage(0)}
                  className="page-link"
                  disabled={!canPreviousPage}
                >
                  <i className="mdi mdi-chevron-left"></i>
                </PaginationLink>
              </PaginationItem>
              {pageOptions.map((page: any, index: any) => (
                <PaginationItem
                  key={index}
                  className={
                    pageIndex === index ? "page-item active" : "page-item"
                  }
                >
                  <PaginationLink
                    onClick={() => gotoPage(index)}
                    className="page-link"
                  >
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem
                className={canNextPage ? "page-item" : "page-item disabled"}
              >
                <PaginationLink
                  onClick={() => gotoPage(pageCount - 1)}
                  className="page-link"
                  disabled={!canNextPage}
                >
                  <i className="mdi mdi-chevron-right"></i>
                </PaginationLink>
              </PaginationItem>
            </ul>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
